import {useCallback, useEffect, useState} from "react";
import {Box, Dialog, IconButton, makeStyles, useTheme} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import {usePanzoom} from "../shared/hooks/usePanzoom";
import {Identifier} from "react-admin";
import {getNoteImage, saveNoteToFile} from "../Distribution/Note/utils";
import RotateRightIcon from '@material-ui/icons/RotateRight';

interface ImageViewerProps {
  open: boolean;
  onClose: () => void;
  noteId: Identifier;
  resource?: string;
  final?: boolean;
  notes?: Array<{ id: Identifier; final?: boolean }>;
}

const useStyles = makeStyles((theme) => ({ paper: { height: "100000000px" } }));

export const ImageViewer = (props: ImageViewerProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const handlePanzoom = usePanzoom();
  const [activeNoteImage, setActiveNoteImage] = useState<string>();
  const [rotation, setRotation] = useState(0);
  const notes = props.notes && props.notes.length
    ? props.notes
    : [{ id: props.noteId, final: props.final }];
  const noteIds = notes.map((note) => note.id);
  const initialNoteIndex = Math.max(0, noteIds.indexOf(props.noteId));
  const [activeNoteIndex, setActiveNoteIndex] = useState(initialNoteIndex);
  const activeNoteId = noteIds[activeNoteIndex] || props.noteId;
  const activeNote = notes[activeNoteIndex] || notes[0];

  useEffect(() => {
    setActiveNoteIndex(Math.max(0, noteIds.indexOf(props.noteId)));
  }, [props.noteId, noteIds.join(",")]);

  const getActiveNoteImage = useCallback(async () => {
    const image = await getNoteImage(activeNoteId, props.resource);
    return image;
  }, [activeNoteId, props.resource]);

  useEffect(() => {
    let isCurrent = true;
    setActiveNoteImage(undefined);
    setRotation(0);
    (async function fetchImage() {
      const image = await getActiveNoteImage();
      if (isCurrent) {
        setActiveNoteImage(image);
      }
    })();
    return () => {
      isCurrent = false;
    };
  }, [getActiveNoteImage]);
  return (
    <Dialog
      classes={classes}
      open={props.open}
      onClose={props.onClose}
      maxWidth="lg"
      style={{ height: "100%" }}
    >
      <div
        style={{
          overflow: "hidden",
          userSelect: "none",
          touchAction: "none",
          height: "100%",
        }}
      >
        <div ref={handlePanzoom} style={{ height: "100%" }}>
          <img
            src={activeNoteImage}
            style={{ width: "100%", height: "100%", transform: `rotate(${rotation}deg)` }}
            alt={"Bolla"}
          />
        </div>
        <Box position={"absolute"} bottom={10} right={10}>
          <IconButton
            style={{ background: theme.palette.primary.main, color: "white" }}
            onClick={() => {
              saveNoteToFile(activeNoteId, props.resource);
            }}
          >
            <GetAppIcon />
          </IconButton>
        </Box>
        <Box position={"absolute"} bottom={10} left={10}>
          <IconButton
            style={{ background: theme.palette.primary.main, color: "white" }}
            onClick={() => {
              setRotation(rotation + 90);
            }}
          >
            <RotateRightIcon />
          </IconButton>
        </Box>
        <Box position={"absolute"} top={10} left="50%" color="white"
             style={{
               background: activeNote.final ? "rgba(76, 175, 80, 0.9)" : "rgba(255, 152, 0, 0.9)",
               borderRadius: 4,
               padding: "8px 12px",
               transform: "translateX(-50%)"
             }}>
          Bolla {activeNote.final ? "finale" : "iniziale"}
          {noteIds.length > 1 && ` · ${activeNoteIndex + 1} di ${noteIds.length}`}
        </Box>
        {noteIds.length > 1 && (
          <>
            <Box position={"absolute"} top={10} left={10}>
              <IconButton
                disabled={activeNoteIndex === 0}
                style={{ background: theme.palette.primary.main, color: "white" }}
                title="Bolla precedente"
                onClick={() => setActiveNoteIndex(activeNoteIndex - 1)}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Box>
            <Box position={"absolute"} top={10} right={10}>
              <IconButton
                disabled={activeNoteIndex === noteIds.length - 1}
                style={{ background: theme.palette.primary.main, color: "white" }}
                title="Bolla successiva"
                onClick={() => setActiveNoteIndex(activeNoteIndex + 1)}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </>
        )}
      </div>
    </Dialog>
  );
};
